/*
 * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {useMemo} from 'react'
import {useAllPages} from '@instructure/platform-query'
import {fetchAssignments, getNextAssignmentsPage} from '../../queries/Queries'
import {groupBy} from 'es-toolkit/compat'
import type {InfiniteData} from '@tanstack/react-query'
import type {FetchAssignmentsResponse} from '../../queries/Queries'

export const useAssignmentsQuery = (courseId: string) => {
  const queryKey: [string, string] = ['individual-gradebook-assignments', courseId]

  const {data, hasNextPage, isError, isLoading} = useAllPages<
    FetchAssignmentsResponse,
    Error,
    InfiniteData<FetchAssignmentsResponse>,
    [string, string]
  >({
    queryKey,
    queryFn: fetchAssignments,
    getNextPageParam: getNextAssignmentsPage,
    initialPageParam: null,
  })

  const flatAssignments = useMemo(
    () => data?.pages.flatMap(page => page.course.assignmentsConnection.nodes) ?? [],
    [data],
  )
  const assignments = useMemo(
    () => groupBy(flatAssignments, 'assignmentGroupId'),
    [flatAssignments],
  )

  return {
    assignments,
    assignmentsLoading: isLoading,
    assignmentsSuccessful: !isError && !isLoading && !hasNextPage,
  }
}
